import React, { useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { AttachmentChip } from './AttachmentChip';
import { AudioRecorder } from './AudioRecorder';
import { useSignedUrls } from './useSignedUrls';
import {
  LIMITS, FILE_ACCEPT, AUDIO_ACCEPT, compressImage, fileMime, audioMime, readAudioDuration, formatBytes
} from '../../../utils/attachmentRules';

const tool = 'inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50';

const makeItem = (kind, blob, name, mime, duration) => ({
  key: crypto.randomUUID(), kind, blob, name, mime, duration, size: blob.size, previewUrl: URL.createObjectURL(blob)
});

export const AttachmentPicker = ({ existing, pending, onAdd, onRemovePending, onRemoveExisting, disabled }) => {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const photoRef = useRef(null);
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const audioRef = useRef(null);
  const urls = useSignedUrls(existing.filter((a) => a.kind !== 'file').map((a) => a.storage_path));

  const room = (kind) =>
    LIMITS[kind].max - existing.filter((a) => a.kind === kind).length - pending.filter((p) => p.kind === kind).length;

  const process = async (event, kind, build) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setError('');
    setBusy(true);
    const items = [];
    const problems = [];
    let left = room(kind);
    for (const file of files) {
      if (left <= 0) { problems.push(`Up to ${LIMITS[kind].max} ${kind === 'audio' ? 'recordings' : kind + 's'} per moment.`); break; }
      try {
        items.push(await build(file));
        left -= 1;
      } catch (err) {
        problems.push(err.message);
      }
    }
    if (items.length) onAdd(items);
    if (problems.length) setError(problems.join(' '));
    setBusy(false);
  };

  const buildPhoto = async (file) => {
    const blob = await compressImage(file);
    if (blob.size > LIMITS.photo.bytes) throw new Error(`${file.name} is too large.`);
    return makeItem('photo', blob, file.name.replace(/\.[^.]+$/, '') + '.jpg', 'image/jpeg', null);
  };

  const buildFile = async (file) => {
    const mime = fileMime(file);
    if (!mime) throw new Error(`${file.name}: use PDF, Word, Excel, text or CSV files.`);
    if (file.size > LIMITS.file.bytes) throw new Error(`${file.name} is over ${formatBytes(LIMITS.file.bytes)}.`);
    return makeItem('file', file, file.name, mime, null);
  };

  const buildAudio = async (file) => {
    const mime = audioMime(file);
    if (!mime) throw new Error(`${file.name}: this audio format is not supported.`);
    if (file.size > LIMITS.audio.bytes) throw new Error(`${file.name} is over ${formatBytes(LIMITS.audio.bytes)}.`);
    const duration = await readAudioDuration(file);
    if (duration && duration > LIMITS.audioSeconds) throw new Error(`${file.name} is longer than 10 minutes.`);
    return makeItem('audio', file, file.name, mime, duration);
  };

  const handleRecorded = ({ blob, mime, duration, name }) => {
    if (room('audio') <= 0) { setError('Up to 3 recordings per moment.'); return; }
    onAdd([makeItem('audio', blob, name, mime, duration)]);
  };

  const off = disabled || busy;
  const rows = [
    ...existing.map((a) => ({ id: a.id, item: { kind: a.kind, name: a.file_name, size: a.size_bytes, duration: a.duration_seconds }, url: urls[a.storage_path], remove: () => onRemoveExisting(a.id) })),
    ...pending.map((p) => ({ id: p.key, item: p, url: p.previewUrl, remove: () => onRemovePending(p.key) }))
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={off} onClick={() => photoRef.current.click()} className={tool}><SafeIcon icon={FiIcons.FiImage} /> Photos</button>
        <button type="button" disabled={off} onClick={() => cameraRef.current.click()} className={tool}><SafeIcon icon={FiIcons.FiCamera} /> Camera</button>
        <button type="button" disabled={off} onClick={() => fileRef.current.click()} className={tool}><SafeIcon icon={FiIcons.FiPaperclip} /> File</button>
        <button type="button" disabled={off} onClick={() => audioRef.current.click()} className={tool}><SafeIcon icon={FiIcons.FiMusic} /> Audio file</button>
        <button type="button" disabled={off || recording} onClick={() => setRecording(true)} className={tool}><SafeIcon icon={FiIcons.FiMic} /> Record</button>
      </div>
      <input ref={photoRef} type="file" accept="image/*" multiple hidden onChange={(e) => process(e, 'photo', buildPhoto)} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => process(e, 'photo', buildPhoto)} />
      <input ref={fileRef} type="file" accept={FILE_ACCEPT} multiple hidden onChange={(e) => process(e, 'file', buildFile)} />
      <input ref={audioRef} type="file" accept={AUDIO_ACCEPT} multiple hidden onChange={(e) => process(e, 'audio', buildAudio)} />
      {recording && <AudioRecorder onRecorded={handleRecorded} onClose={() => setRecording(false)} />}
      {busy && <p className="text-sm text-slate-500">Preparing files…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {rows.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {rows.map((r) => <AttachmentChip key={r.id} item={r.item} url={r.url} onRemove={r.remove} />)}
        </div>
      )}
    </div>
  );
};